import { useRef, useState } from 'react';
import { useRunOnceWhenTruthy } from '../../../common/useRunOnceWhenTruthy';

const POLL_INTERVAL = 50; // ms between DOM checks
const MAX_POLL_ATTEMPTS = 20; // Max attempts before giving up
const OVERLAY_DURATION = 300; // ms to show overlay after search

interface UseSearchOnMountOptions {
  searchTerm: string | undefined;
  isDataReady: boolean;
  sectionSelector: string;
  onSearch: (term: string) => void;
}

/**
 * Automatically searches when component mounts with a search term
 * Shows loading overlay while searching to hide scroll jump
 */
export const useSearchOnMount = ({
  searchTerm,
  isDataReady,
  sectionSelector,
  onSearch,
}: UseSearchOnMountOptions) => {
  const [isSearching, setIsSearching] = useState(false);
  const hasSearchedRef = useRef(false);
  const lastSearchTermRef = useRef<string | undefined>(undefined);

  useRunOnceWhenTruthy(() => {
    if (!searchTerm) {
      return;
    }

    if (hasSearchedRef.current && lastSearchTermRef.current === searchTerm) {
      return;
    }

    setIsSearching(true);
    let attempts = 0;

    const checkAndSearch = () => {
      const element = document.querySelector(sectionSelector);

      if (element) {
        hasSearchedRef.current = true;
        lastSearchTermRef.current = searchTerm;
        onSearch(searchTerm);
        setTimeout(() => setIsSearching(false), OVERLAY_DURATION);
      } else if (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        setTimeout(checkAndSearch, POLL_INTERVAL);
      } else {
        console.warn(`Element ${sectionSelector} not found after polling`);
        setIsSearching(false);
      }
    };

    checkAndSearch();
  }, !!searchTerm && isDataReady);

  return { isSearching };
};
